import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ALWAYS_PRESENT, DomainContext } from '../../domain-repository/DomainRepository'

import { isSafetyExtra } from '../../utils/utils'
import { saveSessionLogs } from '../../services/Metrics'

import { getNewPacks, getPackConfig, PACK_BASIC_EXTRA_ID, PACK_BASIC_EXTRAS, PACK_VARIANTS } from '../../utils/packs-utils'

/**
 * Packs de seguranca novos no kiosk virtual.
 *
 * Porte de kiosk-stand/src/pages/insurance/useSafetyPacksPage.js sem o ramo LEGACY: a pagina legacy
 * do mobile (SafetyPacksPage.js) mantem a sua logica inline e nao passa por aqui.
 *
 * A vertente (INCLUSIVE/EXCLUSIVE) NAO e escolhida pela pagina: e sempre a que o backend calculou
 * (reservation.newSafetyPacksVariant), para que os dois lados nao possam divergir.
 */
export function useSafetyPacksPageV3(onNext, options = {}) {
  const { reservationPackName = 'Base', basicPackName = 'Basic' } = options

  const {
    appFlowConfigs,
    reservationExtras,
    reservation,
    getExcessForExtras,
    computeExtraPrices,
    changeInsurancePack,
    insuranceSelectedPack,
    setInsuranceSelectedPack,
    mapPriceForPack,
    mapExtraPrice,
    getBestPackForExtras,
    calculateExtrasListDayValue,
    getAuth1Value
  } = useContext(DomainContext)

  const safeReservation = reservation || {}

  const [packs, setPacks] = useState([])
  const [selectedPack, setSelectedPack] = useState(insuranceSelectedPack ? insuranceSelectedPack.extraId : null)

  const newPacksVariant = safeReservation.newSafetyPacksVariant || null
  const isExclusiveVariant = newPacksVariant === PACK_VARIANTS.EXCLUSIVE

  /**
   * O PE ja vem na propria reserva (comprado na altura da reserva).
   *
   * A tarifa continua exclusive - o PE nao esta incluido NA TARIFA, que e o que o backend avalia em
   * getNewSafetyPacksVariant - mas o Pack Basico ja esta pago e o cliente nao o pode devolver: deixa
   * de haver opcao "Sem Pack" e e o cartao do Basico que passa a ser o cartao base (e, portanto, o
   * selecionado por defeito).
   */
  const packBasicInReservation = useMemo(
    () => isExclusiveVariant && (reservationExtras || []).some(e => e && e.extraID === PACK_BASIC_EXTRA_ID),
    [isExclusiveVariant, reservationExtras]
  )

  useEffect(() => {
    const allPacks = reservation && reservation.packs ? reservation.packs : []

    // Os packs novos sao identificados pelo proprio codigo em packs-utils e vem de reservation.packs,
    // pelo que a pagina nao depende do conjunto associado ao flow (SAFETY_PACK_GROUP_ID), que
    // continua a servir apenas a pagina legacy.
    const availablePacks = getNewPacks(allPacks, newPacksVariant)

    const configuredPacks = availablePacks.filter(p => {
      return reservation.availableExtras.find(_ => _.extraID === p.extraId) !== undefined
    })

    const packInReservation = reservationExtras.some(
      e => configuredPacks.find(p => p.extraId === e.extraID) !== undefined
    )

    const packIncludedInReservation = reservation.availableExtras
      .filter(e => e.extraIncluded === 'true')
      .some(e => configuredPacks.find(p => p.extraId === e.extraID) !== undefined)

    /**
     * GKIOSK-1889 - NAO ha aqui skip por recusa do Pack Basico (safetyPacksPageBlocked).
     *
     * A recusa no mobile deriva da ausencia do PE na selecao (nao ha estado rejectPackBasic, como no
     * kiosk fisico), e neste flow o PE e vendido NESTA pagina: a entrada, o PE nunca esta selecionado,
     * pelo que o skip disparava sempre e a pagina nunca chegava a aparecer nas reservas exclusive.
     * O bloqueio continua a valer depois da escolha - em canDisplayExtra, para os extras bloqueados.
     */

    // A pagina so pode aparecer se o backend a considerar elegivel.
    const newVariantEligible = Boolean(newPacksVariant)

    // Ao contrario da pagina legacy (que exige um CONJUNTO de packs resolvido), esta so exige que o
    // flow venda packs de seguranca, porque identifica os packs pelo codigo.
    const packsEnabled = Boolean(appFlowConfigs.insurancePacksAllowed)

    /**
     * GKIOSK-1889 - cartao do Pack Basico da vertente exclusive.
     *
     * Sintetizado a partir do extra PE em vez de vir de SAFETY_PACK (ver PACK_BASIC_EXTRA_ID em
     * packs-utils). Fica sempre entre o cartao base e o Essencial, como no design.
     */
    const peExtra = isExclusiveVariant
      ? reservation.availableExtras.find(
          e =>
            e.extraID === PACK_BASIC_EXTRA_ID &&
            // Com o PE ja na reserva nao interessa se continua vendavel: o cartao representa o que a
            // reserva ja traz, tal como o cartao base faz para os restantes extras.
            (packBasicInReservation || (e.available === 'true' && e.extraIncluded === 'false'))
        )
      : undefined

    if (isExclusiveVariant && !peExtra) {
      // Sem /exclusive-reservation no flow, isto deixa a reserva sem forma nenhuma de comprar o Pack
      // Basico - tem de ser diagnosticavel no terreno.
      saveSessionLogs(['Exclusive safety packs page without a sellable PE extra.'])
    }

    const reservationSafetyExtras = reservationExtras
      .filter(Boolean)
      .map(e => e.extraID)
      .filter(isSafetyExtra)

    // Com o PE ja na reserva o cartao do Basico toma o lugar do cartao base: mostra o que a reserva
    // traz (mais o TP/CDW que o PE representa) e nao volta a ser cobrado - o preco fica a 0, como no
    // cartao base, para que trocar para um pack superior nao devolva nada ao cliente.
    const basicPackIsBase = packBasicInReservation && peExtra !== undefined

    const basicPack = peExtra
      ? {
          isBasicPack: true,
          isReservationPack: basicPackIsBase,
          extraId: PACK_BASIC_EXTRA_ID,
          name: basicPackName,
          color: '',
          order: 0,
          extrasList: basicPackIsBase
            ? [...new Set([...reservationSafetyExtras, ...PACK_BASIC_EXTRAS])]
            : PACK_BASIC_EXTRAS,
          ...(basicPackIsBase ? { price: 0 } : {}),
          noDiscountValue: 0,
          showNoDiscountValue: false,
          // Paridade com o PE vendido em /exclusive-reservation: nao removivel no review.
          isUserRemovable: false
        }
      : null

    // O Pack Basico sozinho ja justifica a pagina: se o RentWay nao devolver ESSE/PPE/SPE para esta
    // tarifa, mostram-se na mesma o cartao base e o Basico, em vez de se perder a venda do PE.
    const hasSomethingToSell = configuredPacks.length > 0 || basicPack !== null

    if (packsEnabled && newVariantEligible && hasSomethingToSell && !packIncludedInReservation && !packInReservation) {
      /**
       * Extras com que se calcula a franquia mostrada num cartao.
       *
       * No cartao base injeta-se 'CDW' para a franquia ser a da cobertura de terceiros - mas numa
       * reserva exclusive nao ha CDW nenhum, e a franquia e o valor total da viatura. Nesse caso nao
       * se injeta nada e getExcessForExtras cai no seu fallback (excessNoPriceReduction).
       */
      const excessExtrasForPack = pack => {
        if (!pack.isReservationPack) return [...reservationExtras, { extraID: pack.extraId }]
        if (isExclusiveVariant) return reservationExtras
        return [...reservationExtras, { extraID: 'CDW' }]
      }

      function calculatePriceAndExcessForPacks(_packs) {
        function calculatePrice(pack) {
          if (pack.price || pack.price === 0) return pack.price

          // O Pack Basico nao existe em reservation.packs, pelo que mapPriceForPack devolveria null:
          // preca-se pelo proprio extra, como a /exclusive-reservation ja fazia.
          if (pack.isBasicPack) {
            return mapExtraPrice(reservation.availableExtras.find(e => e.extraID === PACK_BASIC_EXTRA_ID))
          }

          if (insuranceSelectedPack && pack.extraId === insuranceSelectedPack.extraId) {
            return {
              pricePerDay:
                insuranceSelectedPack.price !== null ? insuranceSelectedPack.price : insuranceSelectedPack.totalPrice,
              totalPrice: insuranceSelectedPack.totalPrice
            }
          }

          const value = mapPriceForPack(pack)
          const extra = reservation.availableExtras.find(e => e.extraID === pack.extraId)

          return computeExtraPrices({ ...extra, value })
        }

        return _packs
          .map(pack => {
            const price = calculatePrice(pack)
            return {
              ...pack,
              price: price.pricePerDay || price.totalPrice,
              totalPrice: price.totalPrice,
              showNoDiscountValue: (getPackConfig(pack.extraId) && getPackConfig(pack.extraId).showNoDiscountValue) || false,
              // Soma dos produtos comprados em separado: os ALWAYS_PRESENT (TP/CDW) ja vem na tarifa
              // inclusive, pelo que nao entram no valor rasurado.
              noDiscountValue: pack.isReservationPack
                ? 0
                : calculateExtrasListDayValue((pack.extrasList || []).filter(e => !ALWAYS_PRESENT.includes(e))),
              excess: getExcessForExtras(excessExtrasForPack(pack)),
              auth1: getAuth1Value([{ extraID: pack.extraId }])
            }
          })
          .filter(p => p.price > 0 || p.isReservationPack)
      }

      const reservationPack = {
        isReservationPack: true,
        name: reservationPackName,
        color: '',
        extraId: 'RESERVATION',
        price: 0,
        extrasList: reservationSafetyExtras,
        noDiscountValue: 0,
        showNoDiscountValue: false
      }

      const _packs = calculatePriceAndExcessForPacks([
        // "Sem Pack" so faz sentido enquanto for uma escolha possivel: com o PE ja na reserva o
        // cartao base e o do Pack Basico.
        ...(basicPackIsBase ? [] : [reservationPack]),
        ...(basicPack ? [basicPack] : []),
        ...configuredPacks
          .filter(pack => reservationExtras.find(re => re.extraID === pack.extraId) === undefined)
          .filter(pack => {
            if (insuranceSelectedPack && pack.extraId === insuranceSelectedPack.extraId) return true
            if (
              pack.exclusionExtrasList &&
              pack.exclusionExtrasList.some(e => reservationExtras.find(re => re.extraID === e) !== undefined)
            ) {
              return false
            }

            const packInReservation = getBestPackForExtras(
              reservationExtras.map(e => e.extraID),
              allPacks,
              true
            )
            if (packInReservation) {
              const packExtras = pack.extrasList.filter(e => !ALWAYS_PRESENT.includes(e))
              return !packExtras.every(e => packInReservation.extrasList.find(ec => e === ec))
            }
            return true
          })
          .sort((a, b) => a.order - b.order)
      ])

      if (_packs.filter(p => !p.isReservationPack).length === 0) {
        return onNext(true)
      }
      setPacks(_packs)
    } else {
      onNext(true)
    }
    // Corre uma unica vez, a entrada na pagina (mesmo padrao da pagina legacy e do kiosk fisico):
    // os precos e a franquia sao fixados nesse momento e a selecao passa a ser gerida por estado.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const reservationPack = useMemo(() => {
    return packs.find(p => p.isReservationPack)
  }, [packs])

  // Cartao base pre-selecionado quando ainda nao ha escolha: zero cliques para quem nao quer pack.
  useEffect(() => {
    if (reservationPack && insuranceSelectedPack === null) {
      setSelectedPack(reservationPack.extraId)
    }
  }, [reservationPack]) // eslint-disable-line react-hooks/exhaustive-deps

  const packsWithoutReservation = useMemo(() => {
    return packs.filter(p => !p.isReservationPack)
  }, [packs])

  function onSelectClick(pack) {
    changeInsurancePack(pack.isReservationPack ? null : pack, insuranceSelectedPack)
    setInsuranceSelectedPack(pack.isReservationPack ? null : pack)
    setSelectedPack(pack ? pack.extraId : null)
  }

  const isSelected = useCallback(
    pack => {
      return pack ? pack.extraId === selectedPack : false
    },
    [selectedPack]
  )

  return {
    packs,
    reservationPack,
    packsWithoutReservation,
    selectedPack,
    onSelectClick,
    isSelected,
    newPacksVariant,
    isExclusiveVariant,
    packBasicInReservation
  }
}
